import React, { useState, useEffect } from 'react';
import adminApiService from '../../services/adminApiService';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import './AdminDashboard.css';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getDashboard();
      
      if (response.success) {
        setDashboardData(response);
        console.log('👑 Admin Dashboard loaded:', response);
      } else {
        setError(response.message || 'Failed to load dashboard');
      }
    } catch (error) {
      console.error('❌ Error loading admin dashboard:', error);
      setError(error.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Chart configurations
  const createChartData = (labels, data, colors) => ({
    labels: Array.from(labels || []),
    datasets: [{
      data: Array.from(data || []),
      backgroundColor: colors,
      hoverBackgroundColor: colors.map(color => color.replace('0.8', '1')),
      borderWidth: 2,
      borderColor: '#fff'
    }]
  });

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    cutout: '70%',
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1
      }
    }
  };

  const bookedChartColors = ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e'];
  const cancelledChartColors = ['#e74a3b', '#f6c23e', '#36b9cc', '#8e44ad'];

  if (loading) {
    return (
      <div className="admin-dashboard-container fade-in">
        <div className="container-xl px-4">
          <div className="loading-container">
            <div className="text-center">
              <div className="spinner-border text-danger loading-spinner" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-3">
                <h5 className="text-danger">Loading Admin Dashboard...</h5>
                <p className="text-muted">Please wait while we fetch your data</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard-container fade-in">
        <div className="container-xl px-4">
          <div className="error-container">
            <i className="fas fa-exclamation-triangle fa-3x text-danger mb-3"></i>
            <h4>Error Loading Dashboard</h4>
            <p>{error}</p>
            <button className="btn btn-danger" onClick={loadDashboard}>
              <i className="fas fa-redo me-2"></i>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="admin-dashboard-container">
        <div className="container-xl px-4">
          <div className="error-container">
            <i className="fas fa-info-circle fa-3x text-warning mb-3"></i>
            <h4>No Data Available</h4>
            <p>Dashboard data is not available at the moment.</p>
            <button className="btn btn-danger" onClick={loadDashboard}>
              <i className="fas fa-refresh me-2"></i>
              Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  const {
    stats,
    last12MonthBookings,
    last12MonthCancellations,
    agentCount,
    totalTickets,
    currentMonthStats,
    amountStats,
    monthlyReport,
    todaySummary,
    providerNameBooked,
    providerBookingCountBooked,
    providerLabelsBooked,
    providerCountsBooked,
    providerNameCancelled,
    providerBookingCountCancelled,
    providerLabelsCancelled,
    providerCountsCancelled,
    fundSummary,
    platformFee,
    monthlyPlatformFee
  } = dashboardData;

  return (
    <div className="admin-dashboard-container fade-in">
      {/* Dashboard Header */}
      <div className="container-xl px-4">
        <div className="dashboard-header text-center slide-up">
          <h1><i className="fas fa-tachometer-alt me-3"></i>Admin Dashboard</h1>
          <p className="subtitle">Comprehensive overview of your hotel booking system</p>
        </div>
      </div>

      <div className="container-xl px-4 mt-4">
        {/* Main Stats Cards */}
        <div className="row mb-4">
          <div className="col-lg-6 col-xl-3 mb-4">
            <div className="card stats-card bg-primary text-white h-100 slide-up">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="me-3">
                    <div className="text-white-75 small">Total Agents</div>
                    <div className="text-lg fw-bold">{agentCount || 0}</div>
                  </div>
                  <div className="ms-2">
                    <i className="fas fa-users fa-2x text-gray-200"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-lg-6 col-xl-3 mb-4">
            <div className="card stats-card bg-secondary text-white h-100 slide-up">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="me-3">
                    <div className="text-white-75 small">Total Support Tickets</div>
                    <div className="text-lg fw-bold">{totalTickets || 0}</div>
                  </div>
                  <div className="ms-2">
                    <i className="fas fa-clipboard-list fa-2x text-gray-200"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-lg-6 col-xl-3 mb-4">
            <div className="card stats-card bg-success text-white h-100 slide-up">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="me-3">
                    <div className="text-white-75 small">Total Bookings</div>
                    <div className="text-lg fw-bold">{stats?.totalBookings || 0}</div>
                  </div>
                  <div className="ms-2">
                    <i className="fas fa-calendar-check fa-2x text-gray-200"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-lg-6 col-xl-3 mb-4">
            <div className="card stats-card bg-danger text-white h-100 slide-up">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="me-3">
                    <div className="text-white-75 small">Total Cancellation</div>
                    <div className="text-lg fw-bold">{stats?.totalCancellations || 0}</div>
                  </div>
                  <div className="ms-2">
                    <i className="fas fa-times-circle fa-2x text-gray-200"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 12 Month Stats */}
        <div className="row mb-4">
          <div className="col-xl-3 col-md-6 mb-4">
            <div className="card border-card border-start-lg border-start-primary h-100 fade-in">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <div className="small fw-bold text-primary mb-1">Last 12 Months Total Booking</div>
                    <div className="h5">{last12MonthBookings || 0}</div>
                  </div>
                  <div className="ms-2">
                    <i className="fas fa-users fa-2x text-gray-200"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-xl-3 col-md-6 mb-4">
            <div className="card border-card border-start-lg border-start-secondary h-100 fade-in">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <div className="small fw-bold text-secondary mb-1">Last 12 Months Total Cancellation</div>
                    <div className="h5">{last12MonthCancellations || 0}</div>
                  </div>
                  <div className="ms-2">
                    <i className="fas fa-clipboard-list fa-2x text-gray-200"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-xl-3 col-md-6 mb-4">
            <div className="card border-card border-start-lg border-start-success h-100 fade-in">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <div className="small fw-bold text-success mb-1">Current Month Bookings</div>
                    <div className="h5">{currentMonthStats?.totalCurrentMonthBookings || 0}</div>
                  </div>
                  <div className="ms-2">
                    <i className="fas fa-calendar-check fa-2x text-gray-200"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-xl-3 col-md-6 mb-4">
            <div className="card border-card border-start-lg border-start-danger h-100 fade-in">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <div className="small fw-bold mb-1" style={{ color: 'red' }}>Current Month Cancellations</div>
                    <div className="h5">{currentMonthStats?.totalCurrentMonthCancellations || 0}</div>
                  </div>
                  <div className="ms-2">
                    <i className="fas fa-times-circle fa-2x text-gray-200"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Amount Stats */}
        <div className="row mb-4">
          <div className="col-lg-6 col-xl-3 mb-4">
            <div className="card amount-card bg-primary text-white h-100 slide-up">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="me-3">
                    <div className="text-white-75 small">Last 12 Months Booking Amount</div>
                    <div className="text-lg fw-bold">{amountStats?.formattedLast12MonthsBookingAmount || '₹0.00'}</div>
                  </div>
                  <i className="fas fa-chart-bar fa-2x text-white-50"></i>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-lg-6 col-xl-3 mb-4">
            <div className="card amount-card bg-secondary text-white h-100 slide-up">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="me-3">
                    <div className="text-white-75 small">Last 12 Months Refund Amount</div>
                    <div className="text-lg fw-bold">{amountStats?.formattedLast12MonthsRefundAmount || '₹0.00'}</div>
                  </div>
                  <i className="fas fa-undo fa-2x text-white-50"></i>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-lg-6 col-xl-3 mb-4">
            <div className="card amount-card bg-success text-white h-100 slide-up">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="me-3">
                    <div className="text-white-75 small">Current Month Booking Amount</div>
                    <div className="text-lg fw-bold">{amountStats?.formattedCurrentMonthBookingAmount || '₹0.00'}</div>
                  </div>
                  <i className="fas fa-calendar fa-2x text-white-50"></i>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-lg-6 col-xl-3 mb-4">
            <div className="card amount-card bg-danger text-white h-100 slide-up">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="me-3">
                    <div className="text-white-75 small">Current Month Refund Amount</div>
                    <div className="text-lg fw-bold">{amountStats?.formattedCurrentMonthRefundAmount || '₹0.00'}</div>
                  </div>
                  <i className="fas fa-times-circle fa-2x text-white-50"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Row */}
        <div className="row">
          {/* Left Column */}
          <div className="col-lg-4 mb-5">
            {/* Today's Report */}
            <div className="card today-report-card mb-4 fade-in">
              <div className="card-header">
                <i className="fas fa-calendar-day me-2"></i>
                <b>Today's Reports {todaySummary?.todayDate || ''}</b>
              </div>
              <div className="list-group list-group-flush">
                <div className="list-group-item list-group-item-action">
                  <i className="fas fa-calendar-check fa-fw text-primary me-2"></i>
                  Today's Total Booking: <b>{todaySummary?.totalTodayBookings || 0}</b>
                </div>
                <div className="list-group-item list-group-item-action">
                  <i className="fas fa-coins fa-fw text-success me-2"></i>
                  Today's Total Booking Amount: <b>{todaySummary?.formattedTodayBookingAmount || '₹0.0'}</b>
                </div>
                <div className="list-group-item list-group-item-action">
                  <i className="fas fa-times-circle fa-fw text-danger me-2"></i>
                  Today's Total Booking Cancellation: <b>{todaySummary?.totalTodayCancellations || 0}</b>
                </div>
                <div className="list-group-item list-group-item-action">
                  <i className="fas fa-undo fa-fw text-warning me-2"></i>
                  Today's Total Refund Amount: <b>{todaySummary?.formattedTodayCancellationAmount || '₹0.0'}</b>
                </div>
                <div className="list-group-item list-group-item-action">
                  <i className="fas fa-ticket-alt fa-fw text-info me-2"></i>
                  Today's Total Ticket Generated: <b>{todaySummary?.totalTodayTicketsGenerated || 0}</b>
                </div>
              </div>
            </div>

            {/* Financial Summary Cards */}
            <div className="d-flex flex-column gap-4">
              {/* Total Funds Deposited */}
              <div className="card financial-card bg-success text-white border-0 slide-up">
                <div className="card-body">
                  <h2><i className="fas fa-wallet me-2"></i>Total Funds Deposited this FY</h2>
                  <div className="mb-4">
                    <span className="fs-1 fw-bold text-white">{fundSummary?.formattedFundDeposited || '₹0.00'}</span>
                  </div>
                </div>
              </div>

              {/* Past 12 Months Platform Fees */}
              <div className="card financial-card bg-info text-white border-0 slide-up">
                <div className="card-body">
                  <h2><i className="fas fa-calendar-alt me-2"></i>Past 12 Months Platform Fees</h2>
                  <div className="mb-4">
                    <span className="fs-1 fw-bold text-white">{platformFee?.formattedPlatformFee || '₹0.00'}</span>
                  </div>
                </div>
              </div>

              {/* Current Month Platform Fees */}
              <div className="card financial-card bg-primary text-white border-0 slide-up">
                <div className="card-body">
                  <h2><i className="fas fa-chart-line me-2"></i>Current Month Platform Fees</h2>
                  <div className="mb-4">
                    <span className="fs-1 fw-bold text-white">{monthlyPlatformFee?.currentMonthFormattedPlatformFee || '₹0.00'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="col-lg-8 mb-0">
            {/* Monthly Progress Tracker */}
            <div className="card progress-table-card mb-4 fade-in">
              <div className="card-header">
                <i className="fas fa-chart-line me-2"></i>
                <b>Monthly Progress Tracker</b>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Total Booking</th>
                        <th>Total Booking Cancellation</th>
                        <th>Total Booking Amt</th>
                        <th>Total Cancellation/Refund Amt</th>
                        <th>Total Platform Fee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyReport && monthlyReport.map((report, index) => (
                        <tr key={index}>
                          <td className="text-center">{report.month}</td>
                          <td className="text-center">{report.totalBookings}</td>
                          <td className="text-center">{report.totalCancellations}</td>
                          <td className="text-center fw-bold">{report.formattedBookingAmount}</td>
                          <td className="text-center fw-bold">{report.formattedCancellationAmount}</td>
                          <td className="text-center fw-bold">{report.formattedPlatformFee}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Provider Charts */}
            <div className="row">
              {/* Provider Data For Booking */}
              <div className="col-lg-6">
                <div className="card chart-card h-100 fade-in">
                  <div className="card-header">
                    <i className="fas fa-chart-pie me-2"></i>
                    <b>Provider Data For Booking</b>
                  </div>
                  <div className="card-body">
                    <div className="chart-container">
                      {providerLabelsBooked && providerCountsBooked ? (
                        <Doughnut
                          data={createChartData(providerLabelsBooked, providerCountsBooked, bookedChartColors)}
                          options={chartOptions}
                        />
                      ) : (
                        <div className="no-data-container">
                          <div className="text-muted">No booking data available</div>
                        </div>
                      )}
                    </div>
                    <div className="chart-legend">
                      {providerNameBooked && providerBookingCountBooked && 
                       providerNameBooked.map((label, index) => (
                        <div key={index} className="list-group-item d-flex align-items-center justify-content-between small px-0 py-2">
                          <div className="me-3">
                            <i className="fas fa-circle fa-sm me-1 text-primary"></i>
                            <span className="fs-5 fw-bold text-dark">{label}</span>
                          </div>
                          <div className="fw-500 text-dark">
                            <span className="fs-5 fw-bold text-dark">{providerBookingCountBooked[index]}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Provider Data For Cancellation */}
              <div className="col-lg-6">
                <div className="card chart-card h-100 fade-in">
                  <div className="card-header">
                    <i className="fas fa-chart-pie me-2"></i>
                    <b>Provider Data For Cancellation</b>
                  </div>
                  <div className="card-body">
                    <div className="chart-container">
                      {providerLabelsCancelled && providerCountsCancelled ? (
                        <Doughnut
                          data={createChartData(providerLabelsCancelled, providerCountsCancelled, cancelledChartColors)}
                          options={chartOptions}
                        />
                      ) : (
                        <div className="no-data-container">
                          <div className="text-muted">No cancellation data available</div>
                        </div>
                      )}
                    </div>
                    <div className="chart-legend">
                      {providerNameCancelled && providerBookingCountCancelled && 
                       providerNameCancelled.map((label, index) => (
                        <div key={index} className="list-group-item d-flex align-items-center justify-content-between small px-0 py-2">
                          <div className="me-3">
                            <i className="fas fa-circle fa-sm me-1 text-danger"></i>
                            <span className="fs-5 fw-bold text-dark">{label}</span>
                          </div>
                          <div className="fw-500 text-dark">
                            <span className="fs-5 fw-bold text-dark">{providerBookingCountCancelled[index]}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;